'use client';

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';

import { Checkmark20Regular, ChevronDown20Filled } from '@fluentui/react-icons';
import * as Popover from '@radix-ui/react-popover';
import { X } from 'lucide-react';

import { cn } from '@avodah-inc/ui/utils';

import { ScrollArea } from '../scroll-area';
import { PillList } from '../select/pill';
import { Typography } from '../typography';
import { UnstyledButton } from '../unstyled-button';

type SelectOption = {
  value: string;
  label: string;
  leftElement?: React.ReactNode;
};

export type ComboBoxProps<T extends SelectOption> = {
  multiple?: boolean;
  clearable?: boolean;
  placeholder?: string;
  defaultSelected?: string[];
  label?: string;
  labelTop?: boolean;
  isInvalid?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  size?: 'sm' | 'md';
  showFloatingLabel?: boolean;
  onValueChange?: (value: T[]) => void;
  onInputValueChange?: (value: string) => void;
  onClear?: () => void;
  selectedItems?: T[];
  showPills?: boolean;
  items?: T[];
  className?: string;
  showValue?: boolean;
  checkmark?: boolean;
  searchable?: boolean;
  renderOption?: (item: T) => React.ReactNode;
  classNames?: {
    trigger?: string;
    item?: string;
    label?: string;
    input?: string;
    value?: string;
    icon?: string;
  };
  showControls?: boolean;
  maxPillRows?: number;
  dataTestId?: string;
  deselectAllButtonTestId?: string;
};

export const ComboBox = <T extends SelectOption>({
  items = [],
  multiple = false,
  clearable = true,
  disabled = false,
  label,
  labelTop,
  isInvalid,
  onInputValueChange,
  onValueChange,
  showFloatingLabel: _showFloatingLabel,
  onClear,
  showPills,
  showValue = false,
  searchable = true,
  classNames,
  checkmark = true,
  showControls,
  maxPillRows,
  ...props
}: ComboBoxProps<T>) => {
  const selectionChangeRef = useRef(false);
  const updateTimeoutRef = useRef<NodeJS.Timeout>();
  const [countDisplayText, setCountDisplayText] = useState<string | null>(null);

  const [selectedItems, setSelectedItems] = useState<T[]>(() =>
    items.filter((item: T) =>
      (props.defaultSelected ?? []).includes(item.value),
    ),
  );
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState<string | undefined>('');
  const [shownValue, setShownValue] = useState<string | undefined>('');
  const [lastToggledItem, setLastToggledItem] = useState<string | null>(null);

  const checkmarkRefs = useRef(new Map<string, HTMLDivElement>());
  const pillsContainerRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const popoverItemsRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    setSelectedItems(
      items.filter((item: T) =>
        (props.defaultSelected ?? []).includes(item.value),
      ),
    );
    if (showValue) {
      setShownValue(
        items.find((item: T) =>
          (props.defaultSelected ?? []).includes(item.value),
        )?.label,
      );
    }
  }, [props.defaultSelected, items, showValue]);

  const updateCheckmarkVisibility = useCallback(
    (itemValue: string, isSelected: boolean) => {
      const checkmarkContainer = checkmarkRefs.current.get(itemValue);
      if (checkmarkContainer) {
        if (isSelected) {
          checkmarkContainer.setAttribute('data-force-selected', 'true');
          checkmarkContainer.removeAttribute('data-force-unselected');
        } else {
          checkmarkContainer.setAttribute('data-force-unselected', 'true');
          checkmarkContainer.removeAttribute('data-force-selected');
        }
      }
    },
    [],
  );

  const handleMultipleSelection = useCallback(
    (item: T, prevItems: T[]): T[] => {
      const isSelected = prevItems.some((i) => i.value === item.value);
      if (isSelected) {
        const updatedItems = prevItems.filter((i) => i.value !== item.value);
        requestAnimationFrame(() => {
          updateCheckmarkVisibility(item.value, false);
          onValueChange?.(updatedItems);
        });
        return updatedItems;
      }
      const updatedItems = [...prevItems, item];
      requestAnimationFrame(() => {
        updateCheckmarkVisibility(item.value, true);
        onValueChange?.(updatedItems);
      });
      return updatedItems;
    },
    [onValueChange, updateCheckmarkVisibility],
  );

  const handleSingleSelection = useCallback(
    (item: T) => {
      selectedItems.forEach((selectedItem) => {
        if (selectedItem.value !== item.value) {
          updateCheckmarkVisibility(selectedItem.value, false);
        }
      });

      setSelectedItems([item]);
      requestAnimationFrame(() => {
        updateCheckmarkVisibility(item.value, true);
        onValueChange?.([item]);
      });

      if (showValue) {
        setShownValue(item.label);
      }
      setInputValue('');
      setIsFocused(false);
    },
    [selectedItems, showValue, onValueChange, updateCheckmarkVisibility],
  );

  const handleItemSelected = useCallback(
    async (item: T) => {
      setLastToggledItem(item.value);
      selectionChangeRef.current = true;

      if (multiple) {
        await Promise.resolve().then(() => {
          setSelectedItems((prevItems) =>
            handleMultipleSelection(item, prevItems),
          );
        });
      } else {
        handleSingleSelection(item);
      }

      setTimeout(() => {
        selectionChangeRef.current = false;
        setTimeout(() => {
          if (lastToggledItem === item.value) {
            setLastToggledItem(null);
          }
        }, 100);
      }, 200);
    },
    [multiple, handleMultipleSelection, handleSingleSelection, lastToggledItem],
  );

  useEffect(() => {
    const handleOutSideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsFocused(false);
        setInputValue('');
      }
    };

    window.addEventListener('mousedown', handleOutSideClick);
    return () => {
      window.removeEventListener('mousedown', handleOutSideClick);
    };
  }, []);

  useEffect(() => {
    const updatePopoverWidth = () => {
      if (
        containerRef.current &&
        popoverContentRef.current &&
        popoverItemsRef.current
      ) {
        const triggerWidth = containerRef.current.offsetWidth;
        popoverContentRef.current.style.width = `${triggerWidth}px`;
        popoverItemsRef.current.style.width = `${triggerWidth}px`;
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        if (event.key === 'Tab') {
          setIsFocused(false);
          setInputValue('');
        }
      }
    };

    window.addEventListener('resize', updatePopoverWidth);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('resize', updatePopoverWidth);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    containerRef.current,
    popoverContentRef.current,
    isFocused,
    popoverItemsRef.current,
  ]);

  useEffect(() => {
    setCountDisplayText(`${selectedItems.length} Selected`);
  }, [selectedItems.length]);

  useEffect(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [selectedItems.length]);

  const getInputPlaceholder = () => {
    if (labelTop) return props.placeholder;
    if (showValue && selectedItems.length > 0 && multiple && !isFocused) {
      return `${selectedItems.length} selected`;
    }
    if (showValue && selectedItems.length > 0 && !isFocused) {
      return shownValue || '';
    }
    return undefined;
  };

  const renderListItem = useCallback(
    (item: T) => {
      const isSelectedItem = selectedItems.some(
        (selectedItem) => selectedItem.value === item.value,
      );
      const isLastToggled = item.value === lastToggledItem;
      const stableKey = `item-${item.value}`;

      return (
        <li
          onClick={(e: React.MouseEvent) => {
            if (disabled) return;
            e.preventDefault();
            e.stopPropagation();
            void (async () => {
              await handleItemSelected(item);
            })();
          }}
          key={stableKey}
          data-selected={isSelectedItem ? 'true' : 'false'}
          data-last-toggled={isLastToggled ? 'true' : 'false'}
          data-testid={
            props.dataTestId
              ? `${props.dataTestId}-list-item-${item.value}`
              : undefined
          }
          className={cn(
            'hover:bg-primary-100 relative flex cursor-pointer flex-row items-center justify-between gap-x-2 overflow-hidden truncate whitespace-nowrap px-4 py-2 transition-colors duration-150',
            {
              'bg-primary-50': isSelectedItem && !checkmark,
              'px-3': showControls,
            },
            classNames?.item,
          )}
        >
          <div className="flex min-w-0 flex-1 items-center gap-x-2">
            {item.leftElement}
            {props.renderOption ? (
              props.renderOption(item)
            ) : (
              <Typography
                variant={showControls ? 'body4' : 'body3'}
                className="truncate"
              >
                {item.label}
              </Typography>
            )}
          </div>
          {checkmark && (
            <div
              className={cn(
                'flex h-5 w-5 flex-shrink-0 items-center justify-center',
              )}
              ref={(el) => {
                if (el) {
                  checkmarkRefs.current.set(item.value, el);
                } else {
                  checkmarkRefs.current.delete(item.value);
                }
              }}
            >
              <Checkmark20Regular
                className={cn(
                  'text-primary pointer-events-none transition-opacity duration-150',
                  isSelectedItem ? 'opacity-100' : 'opacity-0',
                )}
              />
            </div>
          )}
        </li>
      );
    },
    [
      selectedItems,
      lastToggledItem,
      disabled,
      checkmark,
      showControls,
      handleItemSelected,
      props.renderOption,
      classNames?.item,
    ],
  );

  const listItems = useMemo(
    () => items.map(renderListItem),
    [items, renderListItem],
  );

  const pillListComponent = useMemo(() => {
    if (!showPills) return null;

    return (
      <div
        ref={pillsContainerRef}
        className="transition-none"
        style={{
          maxHeight: maxPillRows ? `${maxPillRows * 2.5}rem` : 'fit-content',
          opacity: 1,
        }}
      >
        <PillList
          maxRows={maxPillRows}
          list={selectedItems}
          onClick={(item: T) => {
            void (async () => {
              await handleItemSelected(item);
            })();
          }}
          dataTestId={props.dataTestId}
        />
      </div>
    );
  }, [showPills, maxPillRows, selectedItems, handleItemSelected]);

  const controlsSection = useMemo(() => {
    if (!showControls) return null;

    return (
      <div
        className={cn('flex flex-col border-b border-gray-300 px-3 py-2', {
          'gap-y-2': selectedItems.length > 0,
        })}
      >
        <div className="flex flex-row items-center justify-between">
          <UnstyledButton
            onPress={() => {
              if (selectedItems.length > 0) {
                setCountDisplayText('0 Selected');
                setSelectedItems([]);
                onValueChange?.([]);
                setInputValue('');
              }
            }}
            className={cn('text-primary select-none', {
              'text-red-500': selectedItems.length > 0,
            })}
            data-testid={props.deselectAllButtonTestId}
          >
            {selectedItems.length > 0 ? 'Deselect All' : ''}
          </UnstyledButton>
          <div className="selected-count-container">
            <Typography
              variant="body3"
              className={cn(
                'select-none text-gray-500 transition-all duration-150',
              )}
            >
              {countDisplayText || `${selectedItems.length} Selected`}
            </Typography>
          </div>
        </div>
        {selectedItems.length > 0 && (
          <div className="mt-1 flex w-full flex-row items-center justify-between">
            <div className="w-full min-w-0">
              <PillList
                list={selectedItems}
                onClick={(item: T) => {
                  void (async () => {
                    await handleItemSelected(item);
                  })();
                }}
                maxRows={2}
              />
            </div>
          </div>
        )}
      </div>
    );
  }, [showControls, selectedItems, handleItemSelected, countDisplayText]);

  return (
    <div
      className={cn('group w-full space-y-2 outline-none', props.className)}
      ref={containerRef}
      tabIndex={0}
    >
      <Popover.Root open={isFocused && !disabled}>
        <div
          className={cn(
            'bg-base-white group flex rounded border border-gray-300 group-focus:outline group-focus:outline-[1px]',
            {
              'border-red-500': isInvalid,
              'bg-gray-100': disabled,
            },
          )}
        >
          <div
            className="group relative flex w-full flex-row flex-nowrap"
            data-state={isFocused && !disabled ? 'open' : 'closed'}
          >
            <Popover.Trigger
              asChild
              onClick={() => {
                setIsFocused(true);
                setTimeout(() => inputRef.current?.focus(), 0);
              }}
              data-testid={props.dataTestId}
              className={cn(
                'w-full rounded px-4 py-2',
                {
                  '!h-6 rounded-lg py-0': props.size === 'sm',
                },
                classNames?.trigger,
              )}
            >
              <div className="h-full w-full">
                {label && (
                  <span
                    data-placeholder-shown={inputValue ? 'false' : 'true'}
                    data-state={
                      isFocused ||
                      (selectedItems.length > 0 && showValue) ||
                      (multiple && labelTop) ||
                      shownValue
                        ? 'open'
                        : 'closed'
                    }
                    style={{ maxWidth: 'calc(100% - 3.3rem)' }}
                    className={cn(
                      classNames?.label,
                      'opacity-1 absolute left-[1.1rem] h-fit truncate whitespace-nowrap px-0.5 transition-all data-[state=closed]:top-3 data-[state=open]:-top-[0.55rem] data-[state=closed]:bg-transparent data-[state=open]:bg-white data-[state=closed]:text-[1rem] data-[state=open]:text-xs data-[state=closed]:leading-4 data-[state=closed]:text-gray-500 data-[state=open]:text-gray-400',
                    )}
                  >
                    {label}
                  </span>
                )}
                {searchable ? (
                  <input
                    ref={inputRef}
                    type="text"
                    disabled={disabled}
                    onBlur={() => onInputValueChange?.('')}
                    onFocus={() => {
                      setIsFocused(true);
                    }}
                    value={inputValue}
                    placeholder={getInputPlaceholder()}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      onInputValueChange?.(e.target.value);
                    }}
                    className={cn(
                      classNames?.input,
                      'placeholder:text-base-black w-full truncate bg-transparent pr-6 outline-none',
                      {
                        'text-sm leading-5': props.size === 'sm',
                        'placeholder:text-gray-500': labelTop,
                      },
                    )}
                    data-testid={`${props.dataTestId}-input`}
                  />
                ) : (
                  <div
                    data-disabled={disabled}
                    className={cn('h-6 w-full pr-6', classNames?.value)}
                  >
                    <Typography
                      data-disabled={disabled}
                      variant="body3"
                      className="truncate text-inherit"
                    >
                      {shownValue}
                    </Typography>
                  </div>
                )}
              </div>
            </Popover.Trigger>
            <button
              type="button"
              onClick={() => {
                if (clearable && selectedItems.length > 0 && !showControls) {
                  onClear?.();
                  setSelectedItems([]);
                  onValueChange?.([]);
                  setShownValue(undefined);
                  setInputValue('');
                  setIsFocused(false);
                } else {
                  setIsFocused((prev) => !prev);
                }
              }}
              className="absolute bottom-0 right-4 top-0 z-10 outline-none"
            >
              {!showControls && clearable && selectedItems.length > 0 ? (
                <X size={16} />
              ) : (
                <ChevronDown20Filled
                  className={cn(
                    classNames?.icon,
                    'text-gray-400 transition-all group-data-[state=open]:rotate-180',
                  )}
                />
              )}
            </button>
          </div>
          <Popover.Content
            align="center"
            side="bottom"
            ref={popoverContentRef}
            style={{ width: containerRef.current?.offsetWidth }}
            className={cn(
              'bg-base-white z-[999] rounded border border-gray-300 shadow-md outline-none',
              {
                'h-[24rem]': items.length > 8,
              },
            )}
            sideOffset={12}
          >
            {items.length !== 0 ? (
              <ScrollArea
                display="block"
                className={cn('h-fit rounded', {
                  'h-[24rem]': items.length > 8,
                })}
              >
                {controlsSection}
                <ul
                  ref={popoverItemsRef}
                  className={cn('w-full outline-none', {
                    'divide-y divide-gray-300': showControls,
                  })}
                >
                  {listItems}
                </ul>
              </ScrollArea>
            ) : (
              <div className="flex h-full w-full items-center justify-center px-4 py-6">
                <Typography variant="body4">No results found</Typography>
              </div>
            )}
          </Popover.Content>
        </div>
      </Popover.Root>
      {pillListComponent}
    </div>
  );
};
